import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { Button, Checkbox, CircularProgress } from "@mui/material";
import MUITooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { get, includes } from "lodash";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Comments, Doc, Landvision, Sorty } from "../../../common/js/icons";
import { calculateDaysBetween, formatPrice, navigateToLandvision } from "../utils/operations";
import "./Listings.css";
import { NO_CONTENT_PLACEHOLDER } from "./constants";
import schema from "./reviewListingsTables";

const StyledLightButton = styled(Button)({
    fontWeight: "400",
    minWidth: "0px",
    margin: "0px",
    marginRight: "5px",
    padding: "5px 10px",
});

const StyledAlternateContent = styled("div")({
    margin: "auto",
    textAlign: "center",
    marginTop: "6em",
    marginBottom: "6em",
    color: "rgba(0, 0, 0, 0.30)",
    fontSize: "20px",
});

const StyledLb = styled(Link)({
    textDecoration: "none",
});

const StyledListingIdLink = styled("span")({
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
    color: "#2C99D2",
    fontWeight: "600",
    display: "-webkit-box",
    WebkitLineClamp: "2",
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
});

const StyledListingTableWrapper = styled("div")({
    fontFamily: "IBM Plex Sans, sans-serif",
    overflowX: "auto",
});

const StyledTableHeaderRow = styled("div")({
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid rgb(212, 212, 212)",
    color: "#9E9E9E",
    fontSize: "12px",
    fontWeight: "bold",
    padding: "1.5em 0",
    overflow: "hidden",
    scrollbarGutter: "stable",
});

const StyledTableBodyRow = styled("div")({
    display: "flex",
    alignItems: "center",
    borderBottom: "1px solid rgb(212, 212, 212)",
    fontSize: "13px",
    height: "fit-content",
    padding: "0.75em 0",
});

const StyledTableBodyWrapper = styled("div")({
    display: "block",
    maxHeight: "50vh",
    minHeight: "425px",
    overflowY: "auto",
    scrollbarGutter: "stable",
});

const StyledAlertNotificationText = styled("div")({
    color: "red",
    fontSize: "13px",
    fontWeight: "bold",
});

const getTableColumnWidths = (brokerOpenTab, header) => {
    const doubleHeaderWidth = ["Address", "Company", "Links"];
    const tripleHeaderWidth = ["Address", "Links"];
    if (brokerOpenTab && tripleHeaderWidth.indexOf(header.name) !== -1) {
        return "0 1 16.666667%";
    } else if (doubleHeaderWidth.indexOf(header.name) !== -1) {
        return "0 1 16.666667%";
    } else {
        return "0 1 8.333333%";
    }
};

const ListingsWithoutMap = (props) => {
    const {
        currentPath,
        handleCheckboxClick,
        handleDragEnd,
        handleGridSort,
        handleSelectAll,
        handleSelectAllGroup,
        listings,
        loadingSearch,
        listingTab,
        role,
        selectedListings,
        navigateToRequestUpdate,
        openCloseDealDialog,
        selectAll,
        selectAllGroup,
        searchCleared,
        selectedListingsGroup,
        searchRun,
    } = props;

    const navigate = useNavigate();
    const listingsLocal = get(listings, listingTab, {});
    const hasListings = Object.keys(listingsLocal).length > 0;
    const listingsTab = listingTab === "all_km_listings" || listingTab === "listings_group";
    const brokerOpenTab = listingTab === "broker_open";
    const headers = brokerOpenTab ? schema.viewHeaders["broker_open"] : schema.viewHeaders["all_km_listings"];
    const loadRows = brokerOpenTab ? !loadingSearch && hasListings : !loadingSearch && !searchCleared && hasListings;

    const getListStyle = () => ({
        padding: "",
    });

    const onDragEnd = (results) => {
        const { destination, source } = results;
        if (!destination) return;
        if (destination.droppableId === source.droppableId && destination.index === source.index) return;
        handleDragEnd(results, listingsLocal, listingTab);
    };

    return (
        <StyledListingTableWrapper>
            <StyledTableHeaderRow>
                {headers.map((header, i) => {
                    return (
                        <div
                            key={`gh-c${i}`}
                            numeric={header.numeric}
                            onClick={() => handleGridSort(header.name, listingTab)}
                            style={{ paddingRight: "7px", flex: getTableColumnWidths(brokerOpenTab, header) }}
                        >
                            {header.name === "Property" ? (
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    {listingTab === "listings_group" ? (
                                        <Checkbox
                                            className={
                                                selectAllGroup ? "listingCheckboxAllSelected" : "listingCheckboxAll"
                                            }
                                            checked={selectAllGroup}
                                            value={selectAllGroup}
                                            onChange={() => handleSelectAllGroup()}
                                            color="primary"
                                        />
                                    ) : (
                                        <Checkbox
                                            className={selectAll ? "listingCheckboxAllSelected" : "listingCheckboxAll"}
                                            checked={selectAll}
                                            value={selectAll}
                                            onChange={() => handleSelectAll()}
                                            color="primary"
                                        />
                                    )}
                                    <span style={{ paddingLeft: "7px" }}>{header.name.toUpperCase()}</span>
                                </div>
                            ) : header.name === "Links" ? (
                                <div>{header.name.toUpperCase()}</div>
                            ) : (
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    <span style={{ paddingRight: "7px" }}>{header.name.toUpperCase()}</span>
                                    <Sorty />
                                </div>
                            )}
                        </div>
                    );
                })}
            </StyledTableHeaderRow>

            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable" style={{ maxWidth: "100%" }}>
                    {(provided, snapshot) => (
                        <StyledTableBodyWrapper
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            style={getListStyle(snapshot.isDraggingOver)}
                        >
                            {/* All Km Listings */}
                            {listingsTab && loadRows
                                ? listingsLocal.map((listing, i) =>
                                      row(
                                          listing,
                                          i,
                                          headers,
                                          role,
                                          listingTab,
                                          handleCheckboxClick,
                                          selectedListings,
                                          navigateToRequestUpdate,
                                          currentPath,
                                          selectedListingsGroup,
                                          navigateToLandvision,
                                          brokerOpenTab,
                                          openCloseDealDialog,
                                          navigate
                                      )
                                  )
                                : // Broker Listings
                                brokerOpenTab && loadRows
                                ? listingsLocal.map((listing, i) =>
                                      row(
                                          listing,
                                          i,
                                          headers,
                                          role,
                                          listingTab,
                                          handleCheckboxClick,
                                          selectedListings,
                                          navigateToRequestUpdate,
                                          currentPath,
                                          selectedListingsGroup,
                                          navigateToLandvision,
                                          brokerOpenTab,
                                          openCloseDealDialog,
                                          navigate
                                      )
                                  )
                                : alternateContent(listingTab, loadingSearch, hasListings, searchRun)}
                            {provided.placeholder}
                        </StyledTableBodyWrapper>
                    )}
                </Droppable>
            </DragDropContext>
        </StyledListingTableWrapper>
    );
};

const displayAlertNotification = (listing) => {
    const alert =
        get(listing, "status", null) === "Active Broker Action Required" ? (
            <StyledAlertNotificationText>Update Action Required! (See Comments)</StyledAlertNotificationText>
        ) : get(listing, "status", null) === "Update Requested" ? (
            <StyledAlertNotificationText style={{ color: "#FCBA03" }}>Update Requested</StyledAlertNotificationText>
        ) : (
            checkExpiration(listing)
        );
    return alert;
};

const checkExpiration = (listing) => {
    if (get(listing, "autoRenewFlg", false) !== true) {
        const daysBetween = calculateDaysBetween(new Date(listing?.expirationDate));
        if (get(listing, "expired", false) === true) {
            return <StyledAlertNotificationText>Expired</StyledAlertNotificationText>;
        } else if (daysBetween < 61) {
            return (
                <StyledAlertNotificationText>
                    Expires in {daysBetween + 1} {daysBetween === 0 ? `day` : `days`}
                </StyledAlertNotificationText>
            );
        }
    }
};

const row = (
    listing,
    i,
    headers,
    role,
    listingTab,
    handleCheckboxClick,
    selectedListings,
    navigateToRequestUpdate,
    currentPath,
    selectedListingsGroup,
    navigateToLandvision,
    brokerOpenTab,
    openCloseDealDialog,
    navigate
) => {
    const isKmListing = !get(listing, "nonKmListingFlg", null);
    const viewCloseButton =
        brokerOpenTab && isKmListing && includes(["broker", "portlandBrokerAdmin", "arizonaBrokerAdmin", "evp"], role);

    const brokersTooltip = (listingBrokers) => {
        return listingBrokers
            .filter((listingBroker) => get(listingBroker, "brokerAgentKey.person", null) !== null)
            .map(
                (listingBroker) =>
                    get(listingBroker, "brokerAgentKey.person.firstName", "") +
                    " " +
                    get(listingBroker, "brokerAgentKey.person.lastName", "")
            );
    };

    const getItemStyle = (isDragging, draggableStyle) => ({
        background: isDragging ? "#C5C5C5" : "",
        userSelect: "none",
        width: "100%",
        ...draggableStyle,
    });

    const handleRequestUpdate = () => {
        navigateToRequestUpdate(currentPath);
        navigate(`/listing/${listing.listingKey}`);
    };

    const getTableColumnWidths = (brokerOpenTab, header) => {
        const doubleHeaderWidth = ["Address", "Company", "Links"];
        const tripleHeaderWidth = ["Address", "Links"];
        if (brokerOpenTab && tripleHeaderWidth.indexOf(header.name) !== -1) {
            return "0 1 16.666667%";
        } else if (doubleHeaderWidth.indexOf(header.name) !== -1) {
            return "0 1 16.666667%";
        } else {
            return "0 1 8.333333%";
        }
    };

    return (
        <Draggable
            draggableId={`AllListingsWOutMap Draggable - ${String(i)}`}
            index={i}
            style={{ width: "100%", height: "fit-content" }}
            key={`gb-r${i}`}
        >
            {(provided, snapshot) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                >
                    <StyledTableBodyRow>
                        {headers.map((header, j) => {
                            const propertyPhoto =
                                get(listing, "listing_photo", null) !== null
                                    ? get(listing, "listing_photo", null)
                                    : brokerOpenTab
                                    ? get(listing, "primaryPhotoUrl", null)
                                    : get(listing, "propertyPhotoUrl", null);
                            const isKmManaged = get(listing, "property.kmManagedFlg", false);

                            return (
                                <div
                                    key={`gh-c-row-${j}`}
                                    numeric={header.numeric}
                                    style={{ paddingRight: "7px", flex: getTableColumnWidths(brokerOpenTab, header) }}
                                >
                                    {
                                        /* --- Property --- */
                                        header.name === "Property" ? (
                                            <div style={{ display: "flex", alignItems: "center" }}>
                                                {listingTab === "listings_group" ? (
                                                    <Checkbox
                                                        className={
                                                            selectedListingsGroup.get(listing.listingKey) === true
                                                                ? "listingCheckboxAllSelected"
                                                                : "listingCheckboxAll"
                                                        }
                                                        checked={selectedListingsGroup.get(listing.listingKey) === true}
                                                        value={selectedListingsGroup.get(listing.listingKey) === true}
                                                        onChange={() =>
                                                            handleCheckboxClick(listing.listingKey, listingTab)
                                                        }
                                                        color="primary"
                                                    />
                                                ) : (
                                                    <Checkbox
                                                        className={
                                                            selectedListings.get(listing.listingKey) === true
                                                                ? "listingCheckboxAllSelected"
                                                                : "listingCheckboxAll"
                                                        }
                                                        checked={selectedListings.get(listing.listingKey) === true}
                                                        value={selectedListings.get(listing.listingKey) === true}
                                                        onChange={() =>
                                                            handleCheckboxClick(listing.listingKey, listingTab)
                                                        }
                                                        color="primary"
                                                    />
                                                )}
                                                <div
                                                    className="propertySmallImageContainer"
                                                    style={{
                                                        backgroundImage: `url(${propertyPhoto})`,
                                                        marginLeft: "7px",
                                                        width: "85px",
                                                        minWidth: "85px",
                                                        height: "85px",
                                                    }}
                                                >
                                                    {isKmManaged && <span className="listingTag">KM Managed</span>}
                                                </div>
                                            </div>
                                        ) : /* --- Address --- */
                                        header.name === "Address" ? (
                                            <div>
                                                <StyledLb to={`/listing/${listing.listingKey}`} target="_blank">
                                                    <StyledListingIdLink
                                                        style={
                                                            listing.expired ? { color: "red" } : { color: "#2C99D2" }
                                                        }
                                                    >
                                                        {get(listing, "listingName").trim()}
                                                    </StyledListingIdLink>
                                                </StyledLb>
                                                <div>{`${get(listing, "property.primaryAddress.address1", {})}`}</div>
                                                <div>{`${get(listing, "property.primaryAddress.city.city", {})}, ${get(
                                                    listing,
                                                    "property.primaryAddress.city.state.stateCode",
                                                    {}
                                                )}, ${get(
                                                    listing,
                                                    "property.primaryAddress.zipCode.zipCode",
                                                    {}
                                                )}`}</div>
                                                {brokerOpenTab && displayAlertNotification(listing)}
                                            </div>
                                        ) : /* --- Brokers --- */
                                        header.name === "Brokers" ? (
                                            <React.Fragment>
                                                {get(listing, header.prop, null) !== null ? (
                                                    get(listing, header.prop, []).length > 1 ? (
                                                        <MUITooltip
                                                            title={
                                                                brokerOpenTab
                                                                    ? brokersTooltip(get(listing, header.prop))
                                                                    : String(get(listing, header.prop)).replaceAll(
                                                                          ",",
                                                                          ", "
                                                                      )
                                                            }
                                                            placement="top"
                                                        >
                                                            <span>{header.transform(get(listing, header.prop))}</span>
                                                        </MUITooltip>
                                                    ) : (
                                                        <div>
                                                            {brokerOpenTab
                                                                ? brokersTooltip(get(listing, header.prop))
                                                                : get(listing, header.prop)}
                                                        </div>
                                                    )
                                                ) : (
                                                    <div>{get(listing, "listingAgent", "")}</div>
                                                )}
                                            </React.Fragment>
                                        ) : /* --- Company --- */
                                        header.name === "Company" ? (
                                            <div numeric={header.numeric}>
                                                {get(listing, header.prop) !== (null || "")
                                                    ? get(listing, header.prop)
                                                    : "-"}
                                            </div>
                                        ) : /* --- Type --- */
                                        header.name === "Type" ? (
                                            <div numeric={header.numeric}>
                                                {`${
                                                    brokerOpenTab
                                                        ? get(listing, "attributes.useType")
                                                        : get(listing, "useType")
                                                } ${get(listing, header.prop)}`}
                                            </div>
                                        ) : /* --- Rate / Price --- */
                                        header.name === "Rate/Price" ? (
                                            <div numeric={header.numeric}>
                                                {(get(listing, header.prop) === null ||
                                                    get(listing, header.prop) === 0) &&
                                                (get(listing, "listPrice", null) === null ||
                                                    get(listing, "listPrice", null) === 0) ? (
                                                    "Call Broker"
                                                ) : get(listing, "listingType").toLowerCase() === "lease" ? (
                                                    includes(
                                                        ["Office", "Retail", "Healthcare", "Life Science"],
                                                        get(listing, "useType")
                                                    ) &&
                                                    (includes(
                                                        ["WA", "OR", "AZ"],
                                                        get(listing, "property.primaryAddress.city.state.stateCode")
                                                    ) ||
                                                        get(listing, "property.primaryAddress.city.city", {}) ===
                                                            "San Francisco") ? (
                                                        `$${get(listing, "baseRentPerYear").toFixed(2)} /SF/YR`
                                                    ) : (
                                                        `$${get(listing, header.prop).toFixed(2)} /SF/MTH`
                                                    )
                                                ) : (
                                                    <div>{`$${
                                                        get(listing, "listPrice", null) !== null
                                                            ? formatPrice(get(listing, "listPrice", 0))
                                                            : NO_CONTENT_PLACEHOLDER
                                                    }`}</div>
                                                )}
                                            </div>
                                        ) : /* --- Square Feet --- */
                                        header.name === "SQFT" ? (
                                            <div numeric={header.numeric}>
                                                {get(listing, header.prop) !== null
                                                    ? header.transform(get(listing, header.prop))
                                                    : NO_CONTENT_PLACEHOLDER}
                                            </div>
                                        ) : /* --- Date Available --- */
                                        header.name === "Date Avail" ? (
                                            <div numeric={header.numeric}>
                                                {get(listing, header.prop) !== null
                                                    ? header.transform(get(listing, header.prop))
                                                    : NO_CONTENT_PLACEHOLDER}
                                            </div>
                                        ) : /* --- Links --- */
                                        header.name === "Links" ? (
                                            <div style={{ whiteSpace: "nowrap" }}>
                                                <MUITooltip title="Flyer" placement="top">
                                                    <span>
                                                        <StyledLightButton
                                                            disabled={get(listing, "flyerUrl", null) === null}
                                                            onClick={() => window.open(listing.flyerUrl, "_blank")}
                                                            color="primary"
                                                        >
                                                            <Doc
                                                                fill={
                                                                    get(listing, "flyerUrl", null) !== null
                                                                        ? "#2C99D2"
                                                                        : "#9E9E9E"
                                                                }
                                                            />
                                                        </StyledLightButton>
                                                    </span>
                                                </MUITooltip>

                                                <MUITooltip title="Request Update" placement="top">
                                                    <span>
                                                        <StyledLightButton
                                                            onClick={handleRequestUpdate}
                                                            color="primary"
                                                        >
                                                            <Comments
                                                                fill={
                                                                    !brokerOpenTab
                                                                        ? "#2C99D2"
                                                                        : get(listing, "status", null) ===
                                                                          "Active Broker Action Required"
                                                                        ? "red"
                                                                        : get(listing, "status", null) ===
                                                                          "Update Requested"
                                                                        ? "#FCBA03"
                                                                        : "#2C99D2"
                                                                }
                                                            />
                                                        </StyledLightButton>
                                                    </span>
                                                </MUITooltip>

                                                <MUITooltip title="Landvision" placement="top">
                                                    <span>
                                                        <StyledLightButton
                                                            onClick={() =>
                                                                navigateToLandvision(
                                                                    get(listing, "property.propertyKey"),
                                                                    listing.listingKey
                                                                )
                                                            }
                                                            color="primary"
                                                        >
                                                            <Landvision fill="#2C99D2" />
                                                        </StyledLightButton>
                                                    </span>
                                                </MUITooltip>

                                                {viewCloseButton && (
                                                    <MUITooltip title="Close Deal" placement="top">
                                                        <span>
                                                            <span data-tooltip-id="closeDeal">
                                                                <Button
                                                                    variant="outlined"
                                                                    color="primary"
                                                                    onClick={() => openCloseDealDialog(listing)}
                                                                >
                                                                    Close
                                                                </Button>
                                                            </span>
                                                        </span>
                                                    </MUITooltip>
                                                )}
                                            </div>
                                        ) : null
                                    }
                                </div>
                            );
                        })}
                    </StyledTableBodyRow>
                </div>
            )}
        </Draggable>
    );
};

const alternateContent = (listingTab, loadingSearch, hasListings, searchRun) => {
    return (
        <React.Fragment>
            {listingTab === "all_km_listings" &&
                (loadingSearch ? (
                    <StyledAlternateContent>
                        <CircularProgress color="primary" />
                    </StyledAlternateContent>
                ) : !hasListings ? (
                    searchRun ? (
                        <StyledAlternateContent>No Listings</StyledAlternateContent>
                    ) : (
                        <StyledAlternateContent>Please apply a search criteria</StyledAlternateContent>
                    )
                ) : null)}

            {(listingTab === "listings_group" || listingTab === "broker_open") &&
                (loadingSearch ? (
                    <StyledAlternateContent>
                        <CircularProgress color="primary" />
                    </StyledAlternateContent>
                ) : !hasListings ? (
                    <StyledAlternateContent>No Listings</StyledAlternateContent>
                ) : null)}
        </React.Fragment>
    );
};

export default ListingsWithoutMap;
